/**
 * Sendungsverfolgung – dienstleisterunabhängige Statusstufen und Auswertung der DHL-Antworten
 * (Parcel DE Tracking, Post & Parcel Germany: XML mit verschachtelten <data …>-Elementen).
 */

/** Statusstufen nach „Versendet“ – gleich für alle Dienstleister */
export const DELIVERY_STATUSES = ["pre_transit", "in_transit", "out_for_delivery", "pickup_ready", "delivered", "exception", "returned"] as const;
export type DeliveryStatus = (typeof DELIVERY_STATUSES)[number];

export const DELIVERY_STATUS_LABEL: Record<DeliveryStatus, string> = {
  pre_transit: "Angekündigt",
  in_transit: "Unterwegs",
  out_for_delivery: "In Zustellung",
  pickup_ready: "Zur Abholung bereit",
  delivered: "Zugestellt",
  exception: "Zustellproblem",
  returned: "Zurück an Absender",
};

/** Endzustände – danach wird nicht mehr abgefragt */
export const isFinalDelivery = (s: DeliveryStatus | null | undefined) => s === "delivered" || s === "returned";

/** Zusammenfassung über alle Pakete eines Auftrags: Problem vor allem anderen, zugestellt erst, wenn alle zugestellt sind */
export function aggregateDelivery(statuses: (DeliveryStatus | null | undefined)[]): DeliveryStatus | null {
  const s = statuses.filter((x): x is DeliveryStatus => !!x);
  if (!s.length) return null;
  for (const k of ["exception", "returned"] as const) if (s.includes(k)) return k;
  if (s.every((x) => x === "delivered")) return "delivered";
  // das langsamste noch offene Paket bestimmt den Auftrag
  const open = s.filter((x) => x !== "delivered");
  for (const k of ["pre_transit", "in_transit", "out_for_delivery", "pickup_ready"] as const) if (open.includes(k)) return k;
  return "in_transit";
}

// ── DHL-XML ───────────────────────────────────────────────────────────

export interface XmlNode {
  attrs: Record<string, string>;
  children: XmlNode[];
}

const decode = (v: string) =>
  v.replace(/&(#x[0-9a-f]+|#\d+|amp|lt|gt|quot|apos);/gi, (_, e: string) => {
    const k = e.toLowerCase();
    if (k === "amp") return "&";
    if (k === "lt") return "<";
    if (k === "gt") return ">";
    if (k === "quot") return '"';
    if (k === "apos") return "'";
    return String.fromCodePoint(k.startsWith("#x") ? parseInt(k.slice(2), 16) : parseInt(k.slice(1), 10));
  });

/** Die DHL-Antwort besteht nur aus <data>-Elementen mit Attributen – kleiner, strenger Leser dafür */
export function parseDhlXml(xml: string): XmlNode | null {
  const root: XmlNode = { attrs: {}, children: [] };
  const stack: XmlNode[] = [root];
  const tag = /<(\/?)data\b([^>]*?)(\/?)>/g;
  for (let m = tag.exec(xml); m; m = tag.exec(xml)) {
    const [, closing, rawAttrs, selfClosing] = m;
    if (closing) {
      if (stack.length > 1) stack.pop();
      continue;
    }
    const attrs: Record<string, string> = {};
    for (const a of rawAttrs!.matchAll(/([\w:-]+)\s*=\s*(?:"([^"]*)"|'([^']*)')/g)) attrs[a[1]!] = decode(a[2] ?? a[3] ?? "");
    const node: XmlNode = { attrs, children: [] };
    stack[stack.length - 1]!.children.push(node);
    if (!selfClosing) stack.push(node);
  }
  return root.children[0] ?? null;
}

export interface DhlEvent {
  at: string | null;
  text: string;
  ice: string | null;
  ric: string | null;
  code: string | null;
  location: string | null;
  country: string | null;
}

export interface DhlPiece {
  trackingNumber: string;
  statusText: string;
  delivered: boolean;
  events: DhlEvent[];
}

export interface DhlResult {
  /** 0 = ok; 5 = Anmeldung fehlgeschlagen; 100 = keine Daten */
  code: number;
  error: string | null;
  pieces: DhlPiece[];
}

/** „dd.mm.yyyy hh:mm“ (Europe/Berlin) oder ISO → ISO; unbekannt → null */
export function dhlTimestamp(v: string | undefined): string | null {
  if (!v) return null;
  const de = v.match(/^(\d{2})\.(\d{2})\.(\d{4})\s+(\d{2}):(\d{2})(?::(\d{2}))?$/);
  if (de) {
    const [, d, mo, y, h, mi, s] = de;
    const local = `${y}-${mo}-${d}T${h}:${mi}:${s ?? "00"}`;
    return berlinToIso(local);
  }
  const t = Date.parse(v.includes("T") ? v : v.replace(" ", "T"));
  if (Number.isNaN(t)) return null;
  return /[zZ]|[+-]\d{2}:?\d{2}$/.test(v) ? new Date(t).toISOString() : berlinToIso(v.replace(" ", "T").slice(0, 19));
}

/** lokale Berliner Zeit (ohne Zone) → ISO, Sommer-/Winterzeit berücksichtigt */
function berlinToIso(local: string): string | null {
  const asUtc = Date.parse(`${local}Z`);
  if (Number.isNaN(asUtc)) return null;
  const parts = new Intl.DateTimeFormat("en-US", { timeZone: "Europe/Berlin", hourCycle: "h23", year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit" }).formatToParts(new Date(asUtc));
  const g = (t: string) => Number(parts.find((p) => p.type === t)!.value);
  const berlinAsUtc = Date.UTC(g("year"), g("month") - 1, g("day"), g("hour"), g("minute"), g("second"));
  return new Date(asUtc - (berlinAsUtc - asUtc)).toISOString();
}

/** Antwort auf d-get-piece-detail auswerten (eine oder mehrere Sendungen) */
export function parseDhlPieceDetail(xml: string): DhlResult {
  const root = parseDhlXml(xml);
  if (!root) return { code: -1, error: "Antwort nicht lesbar", pieces: [] };
  const code = Number(root.attrs.code ?? "0");
  const error = code === 0 ? null : root.attrs.error || root.attrs["error-status"] || `Fehlercode ${code}`;
  const pieces: DhlPiece[] = [];
  const walk = (n: XmlNode) => {
    const name = n.attrs.name?.toLowerCase();
    if (name === "piece-shipment" || (n.attrs["piece-code"] && name !== "pieceeventlist")) {
      const evNodes: XmlNode[] = [];
      const collect = (x: XmlNode) => {
        for (const c of x.children) {
          if (c.attrs.name?.toLowerCase() === "pieceevent" || c.attrs["event-timestamp"]) evNodes.push(c);
          collect(c);
        }
      };
      collect(n);
      const events = evNodes.map<DhlEvent>((e) => ({
        at: dhlTimestamp(e.attrs["event-timestamp"]),
        text: (e.attrs["event-text"] || e.attrs["event-status"] || "").trim(),
        ice: e.attrs.ice || null,
        ric: e.attrs.ric || null,
        code: e.attrs["standard-event-code"] || null,
        location: e.attrs["event-location"] || null,
        country: e.attrs["event-country"] || null,
      }));
      // neueste zuerst
      events.sort((a, b) => (b.at ?? "").localeCompare(a.at ?? ""));
      const trackingNumber = n.attrs["piece-code"] || n.attrs["searched-piece-code"] || "";
      if (trackingNumber) {
        pieces.push({ trackingNumber, statusText: (n.attrs.status || n.attrs["short-status"] || events[0]?.text || "").trim(), delivered: n.attrs["delivery-event-flag"] === "1", events });
        return;
      }
    }
    n.children.forEach(walk);
  };
  walk(root);
  return { code, error, pieces };
}

/**
 * Status einer DHL-Sendung bestimmen. Vorrang: Zustell-Kennzeichen, dann ICE-Codes des neuesten Ereignisses,
 * dann der Ereignistext. Unbekanntes bleibt „Unterwegs“ – die Rohdaten werden gespeichert, damit sich die
 * Zuordnung an echten Sendungen nachschärfen lässt.
 */
/**
 * DHL kündigt selbst einen neuen Zustellversuch an, die Ursache liegt bei DHL (Krankheit, Unwetter, „unerwartete
 * Umstände“, Zustellung am nächsten Werktag) – der Empfänger muss nichts tun.
 */
export const carrierDelay = (text: string) =>
  /unerwartete(r|n)? umstände|krankheit|unwetter|witterung|höhere(r)? gewalt|(zustellung|zustellversuch) erfolgt (voraussichtlich )?am (nächsten|folgenden) (werk)?tag|(nächsten|folgenden) werktag (erneut )?zugestellt|erneute(r|n)? zustellversuch|neue(r|n)? zustellversuch|unforeseen circumstances|next (working|business) day|delivery will be (re)?attempted/.test(text.toLowerCase());

export function classifyDhl(p: Pick<DhlPiece, "delivered" | "events" | "statusText">): DeliveryStatus {
  const last = p.events[0];
  const text = `${last?.text ?? ""} ${p.statusText}`.toLowerCase();
  const returned = /zurück an (den )?absender|an (den )?absender zurück|rücksendung an den absender|rücklauf|retoure an absender|returned to sender/.test(text) || last?.ice === "RTSND";
  if (returned) return "returned";
  if (p.delivered || last?.ice === "DLVRD") return "delivered";
  if (/abholbereit|zur abholung|in (der |die )?(filiale|packstation|postfiliale|paketshop).*(bereit|eingeliefert zur abholung)|bereit zur abholung|ready for pick/.test(text)) return "pickup_ready";
  // Verzögerung bei DHL selbst (Krankheit, Unwetter …) mit neuem Zustellversuch: kein Zustellproblem, nichts zu tun
  if (carrierDelay(text)) return "in_transit";
  if (last?.ice === "NTDEL" || /nicht zugestellt|konnte nicht zugestellt|zustellung (war )?nicht möglich|adresse (ist )?(falsch|unvollständig|nicht)|empfänger nicht|annahme verweigert|beschädigt|could not be delivered|refused/.test(text)) return "exception";
  if (last?.ice === "LDTMV" || /zustellfahrzeug geladen|in zustellung|wird (heute )?zugestellt|out for delivery/.test(text)) return "out_for_delivery";
  if (!p.events.length || /elektronisch angekündigt|auftragsdaten|daten (wurden )?übermittelt|electronically announced/.test(text)) return p.events.length > 1 ? "in_transit" : "pre_transit";
  return "in_transit";
}

// ── DHL „Shipment Tracking – Unified“ (JSON) – Ersatz ohne Geschäftskunden-Zugang ──

export type UnifiedStatusCode = "pre-transit" | "transit" | "delivered" | "failure" | "unknown";

interface UnifiedEventJson {
  timestamp?: string;
  statusCode?: string;
  status?: string;
  description?: string;
  location?: { address?: { addressLocality?: string; countryCode?: string } };
}

/** Antwort von /track/shipments auswerten – null, wenn die Sendung nicht enthalten ist */
export function parseDhlUnified(json: unknown): (DhlPiece & { statusCode: UnifiedStatusCode }) | null {
  const s = (json as { shipments?: { id?: string; status?: UnifiedEventJson; events?: UnifiedEventJson[] }[] } | null)?.shipments?.[0];
  if (!s) return null;
  const ev = (e: UnifiedEventJson): DhlEvent => ({
    at: dhlTimestamp(e.timestamp),
    text: (e.description || e.status || "").trim(),
    ice: null,
    ric: null,
    code: e.statusCode ?? null,
    location: e.location?.address?.addressLocality ?? null,
    country: e.location?.address?.countryCode ?? null,
  });
  const events = (s.events ?? []).map(ev).sort((a, b) => (b.at ?? "").localeCompare(a.at ?? ""));
  if (!events.length && s.status) events.push(ev(s.status));
  const code = (["pre-transit", "transit", "delivered", "failure", "unknown"] as const).find((c) => c === s.status?.statusCode) ?? "unknown";
  return { trackingNumber: s.id ?? "", statusText: (s.status?.description || s.status?.status || events[0]?.text || "").trim(), delivered: code === "delivered", events, statusCode: code };
}

/** Status aus Unified: Statuscode von DHL plus Textauswertung wie bei Parcel DE */
export function classifyUnified(p: DhlPiece & { statusCode: UnifiedStatusCode }): DeliveryStatus | null {
  if (p.statusCode === "delivered") return "delivered";
  if (p.statusCode === "unknown" && !p.events.length) return null;
  const c = classifyDhl(p);
  // „failure“ meldet DHL auch bei eigenen Verzögerungen mit neuem Zustellversuch – dann bleibt es „unterwegs“
  if (p.statusCode === "failure" && c !== "returned" && !carrierDelay(`${p.events[0]?.text ?? ""} ${p.statusText}`)) return "exception";
  if (p.statusCode === "pre-transit" && (c === "in_transit" || c === "pre_transit")) return "pre_transit";
  return c;
}

/**
 * Wer Versand- und Zustell-Mails an Endkunden verschickt – je Shopify-Shop, Umstellung nur durch Logiful-Admin.
 * shopify: Connect meldet Zustellstufen an Shopify, Shopify verschickt je nach Benachrichtigungs-Einstellung des Shops.
 * klaviyo: Ereignisse gehen an Klaviyo (Tool-Anbindung), an Shopify keine Zustellstufen – keine doppelten Mails.
 * none: nur die Versandbestätigung, die Shopify beim Versenden schickt.
 */
export const DELIVERY_NOTIFICATION_SOURCES = ["none", "shopify", "klaviyo"] as const;
export type DeliveryNotificationSource = (typeof DELIVERY_NOTIFICATION_SOURCES)[number];
export const deliveryNotificationsOf = (config: unknown): DeliveryNotificationSource => {
  const v = (config as { deliveryNotifications?: unknown } | null)?.deliveryNotifications;
  return (DELIVERY_NOTIFICATION_SOURCES as readonly unknown[]).includes(v) ? (v as DeliveryNotificationSource) : "none";
};

export type ShopifyFulfillmentEventStatus = "IN_TRANSIT" | "OUT_FOR_DELIVERY" | "READY_FOR_PICKUP" | "DELIVERED" | "DELAYED" | "FAILURE";

/**
 * Zustellstufe einer Sendung (ggf. mehrere Pakete) für Shopify: Probleme sofort, sonst der Stand des langsamsten
 * Pakets („zugestellt“ erst, wenn alle zugestellt sind). Angekündigt (Label erstellt) wird nicht gemeldet –
 * „versendet“ kennt Shopify schon.
 */
export function shopifyEventFor(packages: DeliveryStatus[]): ShopifyFulfillmentEventStatus | null {
  if (!packages.length) return null;
  if (packages.includes("returned")) return "FAILURE";
  if (packages.includes("exception")) return "DELAYED";
  const rank: Record<string, number> = { pre_transit: 0, in_transit: 1, out_for_delivery: 2, pickup_ready: 2, delivered: 3 };
  const slowest = packages.reduce((a, b) => ((rank[b] ?? 0) < (rank[a] ?? 0) ? b : a));
  return ({ in_transit: "IN_TRANSIT", out_for_delivery: "OUT_FOR_DELIVERY", pickup_ready: "READY_FOR_PICKUP", delivered: "DELIVERED" } as const)[slowest as "in_transit"] ?? null;
}
